import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { FONDEA_HREF, FONDEA_WAITLIST_HREF, WHITE_LABEL_HREF, getHomeV2 } from "@/content/home-v2";
import FondeaLogo from "@/components/fondea/FondeaLogo";
import SolvennLogo from "@/components/solvenn/SolvennLogo";

/** Home hero: one headline, two product paths (FONDEA ~62%, SOLVENN ~38%). */
const HomeHero = (): JSX.Element => {
  const { language } = useLanguage();
  const c = getHomeV2(language).hero;

  return (
    <section className="relative section-band-base pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
      <div className="enterprise-container relative">
        <p className="mb-5 font-mono text-[11px] uppercase tracking-[0.25em] text-primary">[ {c.eyebrow} ]</p>
        <h1 className="max-w-4xl font-display text-4xl font-semibold leading-[1.05] tracking-tight text-foreground md:text-6xl">
          {c.title}
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">{c.subtitle}</p>

        <div className="mt-12 grid gap-5 lg:grid-cols-[1.65fr_1fr]">
          {/* FONDEA — primary path */}
          <article className="relative overflow-hidden rounded-3xl border border-primary/30 bg-card p-7 shadow-xl md:p-10">
            <div className="pointer-events-none absolute -right-24 -top-24 size-72 rounded-full bg-primary/20 blur-3xl" aria-hidden="true" />
            <div className="relative flex flex-wrap items-center justify-between gap-3">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{c.fondea.tag}</span>
            </div>
            <FondeaLogo className="relative mt-6 h-10 w-auto md:h-12" />
            <p className="relative mt-5 font-display text-2xl font-semibold text-foreground md:text-3xl">{c.fondea.slogan}</p>
            <p className="relative mt-4 max-w-xl text-muted-foreground">{c.fondea.body}</p>
            <div className="relative mt-8 flex flex-wrap gap-3">
              <Link
                to={FONDEA_HREF}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                {c.fondea.primary} <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
              <Link
                to={FONDEA_WAITLIST_HREF}
                className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold text-foreground hover:bg-muted"
              >
                {c.fondea.secondary}
              </Link>
            </div>
          </article>

          {/* SOLVENN — institutional path */}
          <article className="flex flex-col rounded-3xl border border-border bg-card/60 p-7 md:p-8">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{c.whiteLabel.tag}</span>
            <SolvennLogo className="mt-6 h-10 w-auto -ml-2" />
            <p className="mt-3 font-display text-xl font-semibold leading-snug text-foreground">{c.whiteLabel.title}</p>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{c.whiteLabel.body}</p>
            <Link
              to={WHITE_LABEL_HREF}
              className="mt-auto inline-flex items-center gap-2 pt-8 text-sm font-semibold text-primary hover:underline"
            >
              {c.whiteLabel.cta} <ArrowUpRight className="size-4" aria-hidden="true" />
            </Link>
          </article>
        </div>
      </div>
    </section>
  );
};

export default HomeHero;
